import { HttpClient, HttpContext, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { Device, DeviceTableRow, DeviceType } from "../../../models/api/deviceManagement/device";
import { map, Observable, forkJoin } from "rxjs";
import { Software } from "../../../models/api/deviceManagement/software";
import { Update, UpdateResponse, UpdateStartResponse } from "../../../models/api/deviceManagement/update";
import { REFERSH } from "../../../interceptors/cache/cache.interceptor";
import { TenantAdministrationService } from "../tenant-administration/tenant-administration.service";

@Injectable({
  providedIn: "root",
})
export class DeviceManagementService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  private tenantService = inject(TenantAdministrationService);

  /* Devices and updates functions: */

  /**
   * Gets a list of devices including tenant referance.
   * @param {string} [deviceType] - The type of device to get. If not provided, all devices are returned.
   * @returns {Observable<DeviceTableRow[]>} - A list of devices.
   */
  public getDevices(deviceType?: string): Observable<DeviceTableRow[]> {
    return forkJoin([this.fetchDevices(deviceType), this.tenantService.getTenants()]).pipe(
      map(([devices, tenants]) => {
        return devices.map((device) => {
          const tenant = tenants.find((t) => t.cores?.map((c) => c.id).includes(device.coreId));
          return { ...device, selected: false, tenant: tenant?.label ?? null };
        });
      })
    );
  }

  /**
   * Fetches a list of devices from the server.
   * If a deviceType is provided, only devices of that type are returned.
   * @param {string} [deviceType] - The type of device to fetch.
   * @returns {Observable<Device[]>} - A list of devices.
   */
  private fetchDevices(deviceType?: string): Observable<Device[]> {
    let params: HttpParams = new HttpParams();
    if (deviceType) {
      params = params.set("deviceType", deviceType);
    }
    return this.http
      .get<Device[]>(`${environment.deviceManagementUrl}/devices`, {
        params,
      })
      .pipe(
        map((devices) => {
          return devices
            .map((deviceAttr) => {
              return { ...deviceAttr, selected: false };
            })
            .sort((a, b) => a.coreId.localeCompare(b.coreId));
        }),
        this.apiErrorHandler.handle("device")
      );
  }

  /**
   * Gets a list updates since or in between two timestamps.
   * @param {number} startTs - The start time of the update period.
   * @param {number} [endTs] - The end time of the update period. If not provided, server returns all updates since startTs.
   * @returns {Observable<Device[]>} - A list of device updates.
   */
  public getDevicesUpdates(startTs: number, endTs?: number): Observable<Update[]> {
    let params: HttpParams = new HttpParams({ fromObject: { startTs } });
    if (endTs) {
      params = params.set("endTs", endTs);
    }

    return this.http
      .get<Update[]>(`${environment.deviceManagementUrl}/devices/update`, {
        params,
      })
      .pipe(
        map((updates) => {
          // Sort updates by startedAt to see the most recent first
          return updates.sort((a, b) => Number(b.startedAt) - Number(a.startedAt));
        }),
        this.apiErrorHandler.handle("update")
      );
  }

  /**
   * Retrieves the update protocol log for the given update ID.
   * @param {string} updateId - The ID of the update to retrieve the protocol log for.
   * @returns {Observable<string>} - The update protocol log as a string.
   */
  public getUpdateProtocol(updateId: string) {
    return this.http
      .get(`${environment.deviceManagementUrl}/devices/update/${updateId}/logs`, {
        responseType: "text",
      })
      .pipe(this.apiErrorHandler.handle("update"));
  }

  /**
   * Trigger update for list of devices.
   * @param {string} version - The software version to update to.
   * @param {DeviceType} deviceType - The type of device to update.
   * @param {string[]} devices - A list of device serial numbers to update.
   * @returns {Observable<UpdateStartResponse>} - The response from the server, containing the updateId.
   */
  public createUpdate(version: string, deviceType: DeviceType, devices: string[]): Observable<UpdateStartResponse> {
    const reqBody = { version, deviceType, devices };

    return this.http
      .post<UpdateStartResponse>(`${environment.deviceManagementUrl}/devices/update`, reqBody)
      .pipe(this.apiErrorHandler.handle("update"));
  }

  /**
   * Stop a running update process.
   * @param {string} updateId - The ID of the update to stop.
   * @returns {Observable<UpdateResponse>} - The response from the server, containing the result of the stopping process.
   */
  public stopUpdate(updateId: string): Observable<UpdateResponse> {
    return this.http
      .delete<UpdateResponse>(`${environment.deviceManagementUrl}/devices/update/${updateId}`)
      .pipe(this.apiErrorHandler.handle("update"));
  }

  /* Software functions: */

  /**
   * Gets a list of software versions from the server.
   * @param {DeviceType} [deviceType] - The type of device to get software versions for. If not provided, all software versions are returned.
   * @param {string} [version] - The specific version of software to get. If not provided, all software versions are returned.
   * @returns {Observable<Software[]>} - A list of software versions.
   */
  public getSoftwares(deviceType?: DeviceType, refresh = false, version?: string): Observable<Software[]> {
    let params: HttpParams = new HttpParams();
    if (deviceType) {
      params = params.set("deviceType", deviceType);
    }
    if (version) {
      params = params.set("version", version);
    }
    return this.http
      .get<Software[]>(`${environment.deviceManagementUrl}/software`, {
        params,
        context: new HttpContext().set(REFERSH, refresh),
      })
      .pipe(this.apiErrorHandler.handle("software"));
  }

  /**
   * Creates a new software version.
   * @param {string} version The version of the software.
   * @param {DeviceType} deviceType The type of device the software is for.
   * @param {File} file1 The first file of the software.
   * @param {File} file2 The second file of the software.
   * @param {string} [remark] The remark for the software version.
   * @returns An observable that emits the newly created software version, or null if the creation failed.
   */
  public createSoftware(version: string, deviceType: DeviceType, file1: File, file2: File, remark?: string) {
    const formData = new FormData();
    formData.append("version", version);
    formData.append("deviceType", deviceType);
    formData.append("file1", file1);
    formData.append("file2", file2);
    if (remark) {
      formData.append("remark", remark);
    }

    return this.http
      .post<Software>(`${environment.deviceManagementUrl}/software`, formData)
      .pipe(this.apiErrorHandler.handle("software"));
  }

  /**
   * Update an existing software version.
   * @param {string} version The version of the software to edit.
   * @param {DeviceType} deviceType The type of device the software is for.
   * @param {string} [remark] The remark for the software version.
   * @returns An observable
   */
  public editSoftware(version: string, deviceType: DeviceType, remark?: string) {
    const reqBody: { version: string; deviceType: DeviceType; remark?: string } = { version, deviceType };

    if (remark) {
      reqBody.remark = remark;
    }
    return this.http
      .put(`${environment.deviceManagementUrl}/software`, reqBody)
      .pipe(this.apiErrorHandler.handle("software"));
  }

  /**
   * Deletes an existing software version.
   * @param {string} versionId The version id of the software to delete.
   * @returns An observable
   */
  public deleteSoftware(versionId: string) {
    return this.http
      .delete<Software>(`${environment.deviceManagementUrl}/software/${versionId}`, {
        headers: new HttpHeaders({
          "Content-Type": "application/json",
        }),
      })
      .pipe(this.apiErrorHandler.handle("software"));
  }
}
